import {
  authResponseSchema,
  championListSchema,
  currentGameResponseSchema,
  gameListResponseSchema,
  gameResponseSchema,
  meResponseSchema,
  okResponseSchema,
  replayResponseSchema,
  statsSchema,
} from '@champdle/contracts';
import type {
  ChampionList,
  GameListResponse,
  GameView,
  LoginRequest,
  RegisterRequest,
  ReplayResponse,
  SessionUser,
  Stats,
} from '@champdle/contracts';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

interface Parser<T> {
  parse(value: unknown): T;
}

export async function request<T>(
  path: string,
  schema: Parser<T>,
  init: RequestInit = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...(init.headers as Record<string, string> | undefined),
      },
    });
  } catch {
    throw new ApiRequestError(0, 'network', 'Cannot reach the server. Check your connection.');
  }
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    /* non-JSON error page from a proxy */
  }
  if (!res.ok) {
    const err = (body as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiRequestError(
      res.status,
      err?.code ?? 'request_failed',
      err?.message ?? 'Something went wrong. Please try again.',
    );
  }
  return schema.parse(body);
}

const post = (path: string, payload?: object): RequestInit => ({
  method: 'POST',
  ...(payload ? { body: JSON.stringify(payload) } : {}),
});

export const api = {
  stats: (): Promise<Stats> => request('/api/stats', statsSchema),
  games: (before: string | null): Promise<GameListResponse> =>
    request(`/api/games?limit=10${before ? `&before=${before}` : ''}`, gameListResponseSchema),
  replay: (id: string): Promise<ReplayResponse> =>
    request(`/api/games/${encodeURIComponent(id)}`, replayResponseSchema),
  me: async (): Promise<SessionUser | null> => (await request('/api/me', meResponseSchema)).user,
  register: async (input: RegisterRequest): Promise<SessionUser> =>
    (await request('/api/auth/register', authResponseSchema, post('/api/auth/register', input)))
      .user,
  login: async (input: LoginRequest): Promise<SessionUser> =>
    (await request('/api/auth/login', authResponseSchema, post('/api/auth/login', input))).user,
  logout: async (): Promise<void> => {
    await request('/api/auth/logout', okResponseSchema, post('/api/auth/logout'));
  },
  deleteAccount: async (password: string): Promise<void> => {
    await request('/api/auth/delete', okResponseSchema, post('/api/auth/delete', { password }));
  },
  champions: (): Promise<ChampionList> => request('/api/champions', championListSchema),
  currentGame: async (): Promise<GameView | null> =>
    (await request('/api/game/current', currentGameResponseSchema)).game,
  startGame: async (): Promise<GameView> =>
    (await request('/api/game', gameResponseSchema, post('/api/game'))).game,
  guess: async (championId: string): Promise<GameView> =>
    (await request('/api/game/guess', gameResponseSchema, post('/api/game/guess', { championId })))
      .game,
  giveUp: async (): Promise<GameView> =>
    (await request('/api/game/giveup', gameResponseSchema, post('/api/game/giveup'))).game,
};

export const avatarUrl = (avatar: string): string => `/avatars/${avatar}`;
